import { plural } from '@cian/utils';
import { defaultTo } from 'ramda';
import * as React from 'react';
import { useSelector } from 'react-redux';

import { getCompanyDescription } from './utils/helpers';
import { ETelemetryEvents } from '../../types/telemetry';
import { useApplicationContext } from '../../utils/applicationContext';
import { BlockErrorBoundary } from '../BlockErrorBoundary';
import { SuperAgentLevelViewContainer } from '../SuperAgentLevelViewContainer';
import { AboutCompany } from 'shared/components/AboutCompany/AboutCompany';
import { selectCompany, selectOffersCount, selectUser } from 'shared/slices';

export const AboutCompanyContainer = () => {
  const company = useSelector(selectCompany);
  const user = useSelector(selectUser);
  const offersCount = useSelector(selectOffersCount);
  const { telemetry } = useApplicationContext();

  const { age, name, avatarUrl, companyName, description, experience, skills, moderationInfo, participations } =
    company;

  const descriptionList = React.useMemo(
    () =>
      getCompanyDescription({
        description: description ?? undefined,
        skills,
        companyName,
      }),
    [companyName, description, skills],
  );

  const dateStats = React.useMemo(() => {
    const stats: { label: string; value: string }[] = [{ label: 'Опыт работы', value: experience || 'не указан' }];

    if (age) {
      stats.push({ label: 'На Циане', value: age });
    }

    stats.push({
      label: 'В работе',
      value:
        offersCount > 0 ? `${offersCount} ${plural(offersCount, ['объект', 'объекта', 'объектов'])}` : 'нет объектов',
    });

    return stats;
  }, [experience, age, offersCount]);

  const showProfileLinkButton = React.useMemo(() => {
    if (user.isAuthenticated && 'realtyUserId' in user) {
      return user.realtyUserId === company.realtyUserId;
    }

    return false;
  }, [company, user]);

  React.useEffect(() => {
    telemetry.increment(ETelemetryEvents.CompanyPageStart);
  }, [telemetry]);

  return (
    <AboutCompany
      descriptionList={descriptionList}
      avatarUrl={avatarUrl}
      participations={participations ?? []}
      name={name}
      superAgent={
        /* Бейдж декоративный, а текст тултипа приходит из Runtime Settings: при значении
           неожиданного типа падение внутри не должно уносить блок «О компании». */
        <BlockErrorBoundary>
          <SuperAgentLevelViewContainer moderationInfo={defaultTo(null, moderationInfo)} />
        </BlockErrorBoundary>
      }
      dateStats={dateStats}
      showProfileLinkButton={showProfileLinkButton}
      isUserIdentifiedByDocuments={Boolean(moderationInfo?.isUserIdentifiedByDocuments)}
    />
  );
};
